import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { AsyncPipe, CommonModule, DatePipe, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { Observable, fromEvent } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { PatientModelDto } from '@shared/models/rx-models/interfaces/patient-model-dto';
import { MatIconModule } from '@angular/material/icon';
import { InfoCellComponent } from '../../components/info-cell/info-cell.component';
import { PatientModule } from '@modules/patient/patient.module';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { AccountIconComponent, CloseEmptyIconComponent, EditIconComponent } from '@itero/ui-toolkit-angular/icons';
import { TranslatePluralPipe } from '@shared/pipes/translate-plural.pipe';
import { AgePipe } from '@shared/pipes/age.pipe';
import { RxIntuitivePatientHeaderFacade } from '@modules/rx-intuitive-patient-header/rx-intuitive-patient-header.facade';
import { DoctorQuery } from '@modules/doctor/state/doctor-query';
import { GenderPipe } from '../../../patient/pipes/gender.pipe';
import type { Maybe } from '@/types/globals';
import { SafeResourceUrl } from '@angular/platform-browser';
import { PatientAppIframeId } from '@shared/models/enums/patient-app-iframe-id.enum';
import { RxIntuitiveDoctorLicenseComponent } from '../../../rx-intuitive-doctor-license/containers/rx-intuitive-doctor-license/rx-intuitive-doctor-license.component';

@Component({
	selector: 'rx-intuitive-patient-header',
	templateUrl: './rx-intuitive-patient-header.component.html',
	styleUrls: ['./rx-intuitive-patient-header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgIf,
		DatePipe,
		AsyncPipe,
		GenderPipe,
		CommonModule,
		MatIconModule,
		PatientModule,
		TranslateModule,
		InfoCellComponent,
		EditIconComponent,
		TlkButtonComponent,
		TranslatePluralPipe,
		AccountIconComponent,
		CloseEmptyIconComponent,
		RxIntuitiveDoctorLicenseComponent
	],
	providers: [RxIntuitivePatientHeaderFacade],
	standalone: true
})
export class RxIntuitivePatientHeaderComponent extends BaseDestroyableComponent implements OnInit {
	private readonly facade = inject(RxIntuitivePatientHeaderFacade);
	private readonly doctorQuery = inject(DoctorQuery);
	private readonly changeDetectorRef = inject(ChangeDetectorRef);
	private readonly agePipe = new AgePipe();
	private readonly compactHeaderBreakpointPx = 1280;

	readonly expandedPatientAppUrl$: Observable<SafeResourceUrl | null> = this.facade.expandedPatientAppUrl$;
	readonly dateFormat$: Observable<string> = this.facade.dateFormat$;
	readonly isLicenseEditable$ = this.doctorQuery.isLicenseEditable$;
	readonly patientAppId = PatientAppIframeId;
	readonly patientExpanded$ = this.facade.patientExpanded$;
	readonly isPatientSectionReadOnly$ = this.facade.isPatientSectionReadOnly$;
	readonly isPatientCleared$ = this.facade.isPatientCleared$;

	patient: Maybe<PatientModelDto> = null;
	patientAge: Maybe<number> = null;
	isPatientCleared = false;
	isCompactHeader = false;

	ngOnInit(): void {
		if (typeof window === 'undefined') {
			return;
		}

		this.facade.initializePatientAppStreams$.pipe(takeUntil(this.componentAlive$)).subscribe();
		this.facade.patientSelectedEvent$.pipe(takeUntil(this.componentAlive$)).subscribe();
		this.initializePatientData();
		this.subscribeToUpdatePatient();
		this.subscribeToCompactHeaderChanges();
	}

	getPatientFullName(patient: PatientModelDto): string {
		const firstName = patient?.FirstName ?? '';
		const lastName = patient?.LastName ?? '';

		if (this.isCompactHeader && lastName.length) {
			return `${firstName} ${lastName[0]}.`.trim();
		}

		return `${firstName} ${lastName}`.trim();
	}

	toggleHeader(): void {
		this.facade.toggleHeader(this.patient);
	}

	private subscribeToUpdatePatient() {
		this.facade.patientUpdatedEvent$
			.pipe(
				filter(isUpdated => isUpdated),
				withLatestFrom(this.facade.patient$),
				tap(([, patient]) => {
					this.setPatient(patient);
					this.facade.closeHeader();
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private initializePatientData(): void {
		this.facade.patient$
			.pipe(
				tap(patient => {
					this.setPatient(patient);
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private setPatient(patient: Maybe<PatientModelDto>): void {
		this.patient = patient;
		this.patientAge = patient?.DateOfBirth ? this.agePipe.transform(patient.DateOfBirth) : null;
		this.changeDetectorRef.markForCheck();
	}

	private subscribeToCompactHeaderChanges(): void {
		fromEvent(window, 'resize')
			.pipe(
				startWith(null),
				map(() => window.innerWidth <= this.compactHeaderBreakpointPx),
				distinctUntilChanged(),
				takeUntil(this.componentAlive$)
			)
			.subscribe(isCompactHeader => {
				this.isCompactHeader = isCompactHeader;
			});
	}
}
